import { breakpoints, isMobile, subscribeToResize } from '@msc-b2c/shared-utils';

const isHovered = (element: HTMLElement): boolean => element.matches(':hover');
let cleanupStripeResizeSubscription: (() => void) | null = null;

const stripeTeaserLogic = (teaser: HTMLElement, hovered = false) => {
  if (teaser.classList.contains('static')) return;

  const teaserContent = teaser.querySelector<HTMLElement>('.cmp-teaser__content')!;
  const expandBtn = teaser.querySelector<HTMLAnchorElement>('.cmp-teaser__content__more');
  const collapseBtn = teaser.querySelector<HTMLAnchorElement>('.cmp-teaser__content__less');
  const experienceFragment = teaser.querySelector<HTMLElement>(
    '.cmp-teaser__content > .cmp-container'
  );

  const isExpanded = experienceFragment?.classList.contains('show');
  if (!isExpanded) {
    collapseBtn?.classList.add('hide');
  }

  if (experienceFragment) teaser.classList.add('expandable');

  const teaserWidth = teaser.offsetWidth;
  const contentWidth = teaserContent?.offsetWidth ?? 0;

  const computedStyle = getComputedStyle(teaserContent);
  const marginRight = parseFloat(computedStyle.marginRight);
  const paddingRight = parseFloat(computedStyle.paddingRight);
  const gap = computedStyle.gap !== 'normal' ? parseFloat(computedStyle.gap) : 0;
  if (experienceFragment === null) return;
  const fragmentComputedStyle = getComputedStyle(experienceFragment as Element);
  const paddingLeft = parseFloat(fragmentComputedStyle.paddingLeft);
  const MOBILE_BREAKPOINT = 1024;

  let fragmentWidth = teaserWidth - contentWidth - gap - marginRight * 2 - paddingLeft;

  if (hovered) {
    const title = teaser.querySelector<HTMLElement>('.cmp-teaser__title');
    const titleWidth = title?.offsetWidth ?? 0;

    fragmentWidth =
      teaserWidth - titleWidth - gap - marginRight * 2 - paddingRight * 2 - paddingLeft;
  }

  if (experienceFragment && window.innerWidth > MOBILE_BREAKPOINT) {
    experienceFragment.style.width = `${fragmentWidth}px`;
  }

  if (window.innerWidth >= MOBILE_BREAKPOINT) {
    teaserContent.style.width = `${teaserWidth - marginRight * 2}px`;
  }

  // Only add event listeners if not already added
  if (expandBtn && !expandBtn.hasAttribute('data-listener-added')) {
    expandBtn.addEventListener('click', () => {
      collapseBtn?.classList.remove('hide');
      expandBtn.classList.add('hide');
      handleExpand(experienceFragment, expandBtn, teaser);
    });
    expandBtn.setAttribute('data-listener-added', 'true');
  }

  if (collapseBtn && !collapseBtn.hasAttribute('data-listener-added')) {
    collapseBtn.addEventListener('click', () => {
      collapseBtn.classList.add('hide');
      if (experienceFragment && window.innerWidth < breakpoints.lg) {
        experienceFragment.style.maxHeight = `${experienceFragment.scrollHeight}px`;
        experienceFragment.offsetHeight;
        requestAnimationFrame(() => {
          experienceFragment.style.maxHeight = '0px';
          experienceFragment.classList.remove('show');
          if (experienceFragment?.classList.contains('formMessageShown'))
            experienceFragment.classList.remove('formMessageShown');
        });
      } else {
        experienceFragment?.classList.remove('show');
      }

      const container = experienceFragment
        ?.closest('.cmp-container')
        ?.parentElement?.closest('.cmp-container') as HTMLElement | null;
      if (container) initialHeight(teaser);

      expandBtn?.classList.remove('hide');
      handleExpand(experienceFragment, null, teaser);
    });
    collapseBtn.setAttribute('data-listener-added', 'true');
  }
  setFormOverflowHeight(teaserContent);
  computeErrorSuccessBoxHeight(teaserContent);
};

// set the msc form container height dynamicaly based on teaser content height minus some clearence
const setFormOverflowHeight = (teaserContent: Element) => {
  const isMobile = window.innerWidth < breakpoints.lg;
  const teaserContentHeight = teaserContent.getBoundingClientRect().height;
  const scrollableFormContent = document.querySelector('.-scrollable-form-content');
  const clearenceOffset = 88;
  if (!scrollableFormContent || isMobile) return;
  const formHeight = teaserContentHeight - clearenceOffset;
  (scrollableFormContent as HTMLElement).style.maxHeight = `${formHeight}px`;
};

// observe (e.g newsletter) when the API message is unhided
// whrn this happens recalculate the teaser container height
// so we make space for the message box too
const computeErrorSuccessBoxHeight = (teaserContent: Element) => {
  const mscForm = teaserContent.querySelector('.cmp-form__form-container');
  if (!mscForm) return;
  const errorContainer = mscForm.querySelector('.cmp-form__error-container > .cmp-form__error');
  const successContainer = mscForm.querySelector(
    '.cmp-form__success-container > .cmp-form__success-generic'
  );
  const isMobile = window.innerWidth < breakpoints.lg;
  const expandableContainer = mscForm.closest('.cmp-container') as HTMLElement | null;

  if (!isMobile) return;

  if (!errorContainer || !successContainer) return;

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      const message = mutation.target as HTMLElement;
      if (!message.classList.contains('hide')) {
        if (!expandableContainer || expandableContainer.classList.contains('formMessageShown'))
          return;
        const messageHeight = message?.parentElement?.getBoundingClientRect().height ?? 0;
        const oldMaxHeight = expandableContainer.style.maxHeight;
        expandableContainer.style.maxHeight = `${parseFloat(oldMaxHeight) + messageHeight}px`;
        expandableContainer.classList.add('formMessageShown');
      }
    }
  });

  // observe both message divs
  observer.observe(errorContainer, {
    attributes: true,
    attributeFilter: ['class'],
  });

  observer.observe(successContainer, {
    attributes: true,
    attributeFilter: ['class'],
  });
};

const handleExpand = (
  fragment: HTMLElement | null,
  expandBtn: HTMLAnchorElement | null,
  teaser: HTMLElement
) => {
  const container = fragment
    ?.closest('.cmp-container')
    ?.parentElement?.closest('.container') as HTMLElement | null;

  // Smaller mobile gap so the teaser expands just enough
  // to fit its content without leaving excessive whitespace
  // before the next component.
  const stripeMobileGap = 30;
  const stripeDesktopGap = 30;

  if (!expandBtn || !fragment || !container) return;

  fragment.classList.add('show');

  if (window.innerWidth < breakpoints.lg) {
    fragment.style.maxHeight = '0px';
    fragment.offsetHeight;
    requestAnimationFrame(() => {
      fragment.style.maxHeight = `${fragment.scrollHeight + stripeDesktopGap}px`;
    });
  }

  let pendingAnimationFrameId: number | null = null;

  const updateContainerHeight = () => {
    pendingAnimationFrameId = null;
    const nextElementInPage = fragment
      .closest('.container .responsivegrid')
      ?.closest('.cmp-container')
      ?.closest('.container .responsivegrid')?.nextElementSibling;

    const distanceBottom =
      nextElementInPage &&
      nextElementInPage.getBoundingClientRect().bottom - fragment.getBoundingClientRect().bottom;

    if (window.innerWidth < breakpoints.lg) {
      // Use getBoundingClientRect so height is correct regardless of offsetParent.
      // Use scrollHeight so we reserve space for full content (avoids overlap during animation).
      const containerTop = container.getBoundingClientRect().top;
      const fragmentTop = fragment.getBoundingClientRect().top;
      const fragmentFullHeight = Math.max(fragment.offsetHeight, fragment.scrollHeight);
      const requiredHeight = fragmentTop - containerTop + fragmentFullHeight + stripeMobileGap;
      container.style.height = `${Math.ceil(requiredHeight)}px`;
      return;
    }

    if (distanceBottom) {
      container.style.height = `${
        teaser.offsetHeight + Math.abs(distanceBottom) + stripeDesktopGap
      }px`;
    }
  };

  const scheduleHeightUpdate = () => {
    if (pendingAnimationFrameId !== null) return;
    pendingAnimationFrameId = window.requestAnimationFrame(updateContainerHeight);
  };

  const fragmentResizeObserver = new ResizeObserver(scheduleHeightUpdate);

  // start immediately
  fragmentResizeObserver.observe(fragment);
  updateContainerHeight();

  const handleFragmentTransitionEnd = (event: TransitionEvent) => {
    if (
      event.target === fragment &&
      (event.propertyName === 'max-height' || event.propertyName === 'opacity')
    ) {
      fragmentResizeObserver.disconnect();

      if (pendingAnimationFrameId !== null) {
        cancelAnimationFrame(pendingAnimationFrameId);
        pendingAnimationFrameId = null;
      }

      fragment.removeEventListener('transitionend', handleFragmentTransitionEnd);
    }
  };

  fragment.addEventListener('transitionend', handleFragmentTransitionEnd);
};

let isTransitioning = false;

const addResizeObserverOnce = (teaser: HTMLElement) => {
  const content = teaser.querySelector('.cmp-teaser__content');
  if (!content) return;

  const observer = new ResizeObserver(() => {
    stripeTeaserLogic(teaser);
    observer.unobserve(content);
  });

  observer.observe(content);
};

const addResizeObserver = (teaser: HTMLElement) => {
  const observer = new ResizeObserver(() => {
    if (!isTransitioning) {
      stripeTeaserLogic(teaser, isHovered(teaser));
    }
  });

  observer.observe(teaser);
};

const handleOnHover = (teaser: HTMLElement) => {
  const content = teaser.querySelector('.cmp-teaser__content') as HTMLElement;
  if (!content) return;

  let transitionCount = 0;
  content.addEventListener('transitionstart', () => {
    isTransitioning = true;
  });

  content.addEventListener('transitionend', (event: TransitionEvent) => {
    if (['max-width', 'padding'].includes(event.propertyName)) {
      transitionCount++;
      const shouldCheckTeaser = teaser.classList.contains('expandable');
      stripeTeaserLogic(teaser, shouldCheckTeaser ? isHovered(teaser) : isHovered(content));
      if (transitionCount === 2) {
        transitionCount = 0;
        isTransitioning = false;
      }
    }
  });
};

const preventStripeClicks = (teasers: HTMLElement[]) => {
  const stripeTeasers = teasers.filter(
    (teaser) =>
      teaser.classList.contains('cmp-teaser__stripe-right') ||
      teaser.classList.contains('cmp-teaser__stripe-left')
  );

  stripeTeasers.forEach((teaser) => {
    const content = teaser.querySelector('.cmp-teaser__content');
    const actionLink = content?.querySelector('.cmp-teaser__action-link');

    if (!content || !actionLink) return;

    content.addEventListener(
      'click',
      (event) => {
        let current = event.target as HTMLElement;
        let isLinkClicked = false;

        while (current && current !== content) {
          if (current === actionLink) {
            isLinkClicked = true;
            break;
          }
          current = current.parentElement!;
        }

        if (!isLinkClicked) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  });
};

// Map to store the starting heights of teasers
const teaserStartingHeights = new Map<HTMLElement, number>();

const initialHeight = (teaser: HTMLElement) => {
  // Check if we already have a stored height for this teaser
  const storedHeight = teaserStartingHeights.get(teaser);

  if (storedHeight !== undefined) {
    const parentContainer = teaser.closest('.container');
    if (parentContainer) {
      (parentContainer as HTMLElement).style.height = `${storedHeight}px`;
    }
    return;
  }

  // Calculate and store the height for the first time
  if (window.innerWidth < breakpoints.lg) {
    const image = teaser.querySelector('.cmp-teaser__image');
    const content = teaser.querySelector('.cmp-teaser__content');
    const parentContainer = teaser.closest('.container');

    const formFragment = teaser.querySelector<HTMLElement>('.cmp-teaser__content > .cmp-container');
    if (formFragment && !formFragment.classList.contains('show')) {
      formFragment.style.overflow = 'hidden';
    }

    const imageHeight = image?.getBoundingClientRect().height;
    const contentHeight = content?.getBoundingClientRect().height;

    if (parentContainer && (imageHeight != null || contentHeight != null)) {
      const containerHeight = (imageHeight || 0) + (contentHeight || 0);
      (parentContainer as HTMLElement).style.height = `${containerHeight}px`;
      teaserStartingHeights.set(teaser, containerHeight);
    }
  }
};

let lastResizeWidth = window.innerWidth;

const handleGlobalResize = (teasers: HTMLElement[], width: number) => {
  if (width === lastResizeWidth) return;
  lastResizeWidth = width;

  teasers.forEach((teaser) => {
    const parentContainer = teaser.closest('.container') as HTMLElement | null;

    if (isMobile()) {
      // For real width changes (rotation), recalculate base height
      teaserStartingHeights.delete(teaser);
      initialHeight(teaser);
    } else if (parentContainer) {
      parentContainer.style.removeProperty('height');
    }
  });
};

// Public setup function
export const setupStripeTeasers = (teasers: HTMLElement[]) => {
  teasers.forEach((teaser) => {
    stripeTeaserLogic(teaser);
    addResizeObserverOnce(teaser);
    addResizeObserver(teaser);
    handleOnHover(teaser);
    initialHeight(teaser);
  });
  preventStripeClicks(teasers);
  // Subscribe to global resize manager
  cleanupStripeResizeSubscription?.();
  cleanupStripeResizeSubscription = subscribeToResize(({ width }) => {
    const hasConnectedTeasers = teasers.some((teaser) => teaser.isConnected);
    if (!hasConnectedTeasers) {
      cleanupStripeResizeSubscription?.();
      cleanupStripeResizeSubscription = null;
      return;
    }
    handleGlobalResize(teasers, width);
  });
};
